import ZoomMatrix from './ZoomMatrix';
import ViewportState from './ViewportState';
import config from './config';

let isZooming = false;
let isDragging = false;
let offset = { x: 0, y: 0 };
let matrix = new DOMMatrix();

const addMapEvents = (canvas: any, viewport: any) => {
  canvas.current.addEventListener(
    'pointerdown',
    function (event: PointerEvent) {
      if (!isZooming) {
        isDragging = true;
        offset = { x: event.offsetX, y: event.offsetY };
      }
    },
  );

  canvas.current.addEventListener('touchstart', function (event: TouchEvent) {
    if (!isZooming) {
      isDragging = true;
      for (let i = 0; i < event.touches.length; i++) {
        offset = {
          x: event.targetTouches[i].clientX,
          y: event.targetTouches[i].clientY,
        };
      }
    }
  });

  canvas.current.addEventListener('pointerup', function () {
    if (!isZooming) {
      isDragging = false;
    }
  });

  canvas.current.addEventListener('touchend', function () {
    if (!isZooming) {
      isDragging = false;
    }
  });

  canvas.current.addEventListener(
    'pointermove',
    function (event: PointerEvent) {
      if (!isZooming && isDragging) {
        const tx = (event.offsetX - offset.x) * 3.5;
        const ty = (event.offsetY - offset.y) * 3.5;
        offset = {
          x: event.offsetX,
          y: event.offsetY,
        };
        matrix.preMultiplySelf(new DOMMatrix().translateSelf(tx, ty));

        viewport.current.style.transform = ZoomMatrix(matrix).toString();
        ViewportState(viewport.current, matrix);
      }
    },
  );

  canvas.current.addEventListener('touchmove', function (event: TouchEvent) {
    if (!isZooming && isDragging) {
      for (let i = 0; i < event.touches.length; i++) {
        const tx = (event.targetTouches[i].clientX - offset.x) * 3.5;
        const ty = (event.targetTouches[i].clientY - offset.y) * 3.5;
        offset = {
          x: event.targetTouches[i].clientX,
          y: event.targetTouches[i].clientY,
        };
        matrix.preMultiplySelf(new DOMMatrix().translateSelf(tx, ty));

        viewport.current.style.transform = ZoomMatrix(matrix).toString();
        ViewportState(viewport.current, matrix);
      }
    }
  });
};

const centerMap = async (viewport: any, x: number, y: number) => {
  const scale = matrix.a;
  const canvasWidth = config.canvas.width;
  const mapPositionX = matrix.e / scale;
  const centerOfScreenX = mapPositionX - canvasWidth * 0.5;
  const sideX = centerOfScreenX < x ? 'left' : 'right';

  let finalX = x;
  const finalY = y;

  if (sideX === 'left' && x > canvasWidth * 0.25) {
    finalX -= canvasWidth;
    matrix.e -= canvasWidth;
    viewport.current.style.transform = ZoomMatrix(matrix).toString();
    await new Promise((resolve) => {
      window.setTimeout(resolve, 100);
    });
  } else if (sideX === 'right' && x < -canvasWidth * 1.25) {
    finalX += canvasWidth;
    matrix.e += canvasWidth;
    viewport.current.style.transform = ZoomMatrix(matrix).toString();
    await new Promise((resolve) => {
      window.setTimeout(resolve, 100);
    });
  }

  return {
    realX: finalX,
    realY: finalY,
  };
};

const findCenterOfScreen = () => {
  const scale = matrix.a;

  const mapPositionX = matrix.e / scale;
  const mapPositionY = matrix.f / scale;

  const halfCanvasWidth = config.canvas.width * 0.5;
  const halfCanvasHeight = config.canvas.height * 0.5;

  const centerOfScreenX = mapPositionX - halfCanvasWidth;
  const centerOfScreenY = mapPositionY - halfCanvasHeight;

  return {
    centerOfScreenX,
    centerOfScreenY,
  };
};

const animate = (callback: any) => {
  isZooming = true;
  let steps = 0;

  let timer: number;

  const runAnimation = () => {
    timer = window.setTimeout(() => {
      steps++;

      callback();

      window.clearTimeout(timer);
      if (steps < config.animation.steps) {
        runAnimation();
      } else {
        isZooming = false;
      }
    }, config.animation.interval);
  };

  runAnimation();
};

const zoom = async (viewport: any, zoomDirection = 1) => {
  const { centerOfScreenX, centerOfScreenY } = findCenterOfScreen();

  const { realX, realY } = await centerMap(
    viewport,
    centerOfScreenX,
    centerOfScreenY,
  );

  let finalX = realX;
  let finalY = realY;

  if (zoomDirection === -1) {
    finalX -= config.zoom.outMagicNumber;
    finalY = -config.canvas.height * 0.5;
  }

  const animation = () => {
    const newScale = 1 + 0.05 * zoomDirection;
    const nextScale = matrix.a * newScale;

    if (nextScale <= config.zoom.out) {
      matrix.a = config.zoom.out;
      matrix.d = config.zoom.out;
      matrix = ZoomMatrix(matrix);
      matrix.e += 57.75;
    } else {
      matrix = ZoomMatrix(
        matrix.scaleSelf(newScale, newScale, 1, -finalX, -finalY),
      );
    }

    viewport.current.style.transform = matrix.toString();
    ViewportState(viewport.current, matrix);
  };

  animate(animation);
};

const zoomIn = (viewport: any) => {
  const zoomDirection = 1;

  zoom(viewport, zoomDirection);
};

const zoomOut = (viewport: any) => {
  const zoomDirection = -1;

  zoom(viewport, zoomDirection);
};

const zoomClick = async (viewport: any, x: number, y: number) => {
  const { realX, realY } = await centerMap(viewport, -x, -y);

  const animation = () => {
    const newScale = 1 + 0.05;

    matrix = ZoomMatrix(
      matrix.scaleSelf(newScale, newScale, 1, -realX, -realY),
    );

    viewport.current.style.transform = matrix.toString();
    ViewportState(viewport.current, matrix);
  };

  animate(animation);
};

export {
  addMapEvents,
  zoomIn as mapZoomIn,
  zoomOut as mapZoomOut,
  zoomClick as mapZoomClick,
};
